/**
 * 通知パネルの表示整形 helper (#297)
 *
 * 通知 item の timestamp を「日付のみ」から「日付 + 時刻」に拡張する。
 * 同日に通知が多数並ぶ場合でも発生順 (新旧) を時刻で把握できるようにする。
 *
 * - format: `YYYY/M/D H:mm` (例: 2026/5/29 10:39 / 2026/5/29 9:05)
 * - timezone: Asia/Tokyo 固定 (UTC のまま見えないように)
 * - minute は 0 埋め、hour は非 0 埋め (H:mm)
 * - null / undefined / invalid date は空文字 (Invalid Date / raw ISO を出さない)
 */

import type { HandleKey, Locale, TranslationParams } from '@/i18n';
import { translate } from '@/i18n/translate';
import type { notificationsNs } from '@/i18n/namespaces/notifications';
// 名前空間 `notifications` を登録する (日本語の文もこの辞書から描く)
import '@/i18n/namespaces/notifications';

const TZ = 'Asia/Tokyo';

/**
 * 通知 createdAt (ISO 文字列) を「YYYY/M/D H:mm」(JST) に整形する pure 関数。
 * 不正値・未設定時は空文字を返し、UI を壊さない。
 */
export function formatNotificationTimestamp(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const datePart = d.toLocaleDateString('ja-JP', {
    timeZone: TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const timePart = d.toLocaleTimeString('ja-JP', {
    timeZone: TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

// ==============================
// 連続通知の集約表示 (steering 486)
// ==============================

/**
 * グループ表示の入力。`Notification` そのものを要求せず、必要な field だけを受ける
 * (legacy レコードや部分オブジェクトでも安全に扱えるよう意図的に緩くしている)。
 */
export interface NotificationGroupInput {
  type?: string | null;
  title?: string | null;
  clientName?: string | null;
  count?: number | null;
  firstAt?: string | null;
  lastAt?: string | null;
  mixedResource?: boolean | null;
  resourceName?: string | null;
}

/** 通知の文言キー (`i18n/locales/{ja,en}/notifications.ts`)。 */
type NotificationsKey = HandleKey<typeof notificationsNs>;

/**
 * 表示言語に依らない通知の文 (辞書キー + 差し込み値)。画面は {@link renderNotificationText} で描く。
 * 依頼者名・書類名は差し込み値 (データ) としてそのまま渡し、訳さない。
 */
export interface NotificationTextDescriptor {
  key: NotificationsKey;
  params: TranslationParams;
}

/** 通知の文を表示言語で描く (日本語は従来の文と同じ)。 */
export function renderNotificationText(
  descriptor: NotificationTextDescriptor,
  locale: Locale = 'ja',
): string {
  return translate(locale, `notifications.${descriptor.key}`, descriptor.params);
}

/** 件数付きタイトルの辞書キー (種別ごと)。 */
interface GroupTitleKeys {
  /** グループ内の書類が 1 種類に定まるとき */
  named: NotificationsKey;
  /** 書類名を出せないとき (複数種別が混在 / resourceName 欠落) */
  generic: NotificationsKey;
}

/**
 * 種別ごとの件数付きタイトル。
 *
 * **部分マップ + fallback** にしてある。ここに無い種別は必ず元の `title` をそのまま返すため、
 * `NotificationType` が増えても表示が壊れない (CLAUDE.md §2.4「未マップ値フォールバック必須」)。
 * 集約対象は BE の allowlist が決めるので、ここに載せるだけでは集約は起きない。
 * 文面は辞書 (`notifications.group.*`) にあり、日本語は従来の文と同じ。
 */
const GROUP_TITLE_KEYS: Record<string, GroupTitleKeys> = {
  'item.uploaded': { named: 'group.itemUploadedNamed', generic: 'group.itemUploadedGeneric' },
  'collection.requester_item_added': {
    named: 'group.itemAddedNamed',
    generic: 'group.itemAddedGeneric',
  },
  // steering 630: 集約対象ではないので count は常に 1 (文面には出さない)
  'collection.requester_item_deferred': {
    named: 'group.itemDeferredNamed',
    generic: 'group.itemDeferredGeneric',
  },
  'collection.requester_extension_requested': {
    named: 'group.extensionRequestedNamed',
    generic: 'group.extensionRequestedGeneric',
  },
  // notification-message-receive-grouping: ポータルからの受信メッセージ。
  // 単数形の `${clientName}様からメッセージを受信しました` (portal-api.ts) と語尾を揃える。
  // メッセージに「書類名」の概念は無いため **named も generic と同じ文面**にし、
  // 万一 `resourceName` が付いていても書類名を出さない (誤表示を構造的に防ぐ)。
  'portal.message_received': { named: 'group.messagesReceived', generic: 'group.messagesReceived' },
};

/** 集約件数。未設定・不正値は 1 件 (= 集約なし) として扱う。 */
export function notificationGroupCount(n: NotificationGroupInput | null | undefined): number {
  const raw = n?.count;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 1) return 1;
  return Math.floor(raw);
}

function trimmed(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 件数付きタイトルの文 (辞書キー + 差し込み値)。件数付きにしないとき (1 件 / 未マップ種別 /
 * clientName 欠落) は null — 呼び出し側は元の `title` を出す。
 *
 * **title の文字列解析はしない。** 判定に使うのは構造化された field だけ。
 */
export function groupedNotificationTitleDescriptor(
  n: NotificationGroupInput | null | undefined,
): NotificationTextDescriptor | null {
  const count = notificationGroupCount(n);
  if (count <= 1) return null;

  const keys = GROUP_TITLE_KEYS[trimmed(n?.type)];
  if (!keys) return null;

  const clientName = trimmed(n?.clientName);
  if (!clientName) return null;

  const resourceName = trimmed(n?.resourceName);
  if (n?.mixedResource === true || !resourceName) {
    return { key: keys.generic, params: { client: clientName, count } };
  }
  return { key: keys.named, params: { client: clientName, resource: resourceName, count } };
}

/**
 * 通知カードに出すタイトルを返す。
 *
 * - 1 件のとき: **元の title をそのまま**返す (既存表示を変えない。BE の文 = データ)
 * - 複数件で書類が 1 種類: 書類名を保った件数付きタイトル
 * - 複数件で書類が混在 (`mixedResource`) / `resourceName` 欠落: 書類名を出さない汎化タイトル
 * - 種別が未マップ / clientName 欠落: 元の title へ fallback (例外を出さない)
 *
 * `locale` は表示言語 (既定は日本語 = 従来の文)。
 */
export function formatGroupedNotificationTitle(
  n: NotificationGroupInput | null | undefined,
  locale: Locale = 'ja',
): string {
  const descriptor = groupedNotificationTitleDescriptor(n);
  return descriptor ? renderNotificationText(descriptor, locale) : trimmed(n?.title);
}

/**
 * 集約通知の発生時間帯 (`firstAt`〜`lastAt`) を返す。1 件のときや不正値では空文字。
 *
 * 同一日なら終了側の日付を省いて `2026/8/18 6:51〜6:53` の形にする。
 */
export function formatNotificationTimeRange(n: NotificationGroupInput | null | undefined): string {
  if (notificationGroupCount(n) <= 1) return '';
  const start = formatNotificationTimestamp(n?.firstAt);
  const end = formatNotificationTimestamp(n?.lastAt);
  if (!start || !end) return '';
  if (start === end) return start;

  const [startDate] = start.split(' ');
  const [endDate, endTime] = end.split(' ');
  if (startDate && endDate && startDate === endDate && endTime) {
    return `${start}〜${endTime}`;
  }
  return `${start}〜${end}`;
}

// ==============================
// 本文プレビュー行の重複抑止 (notification-drawer-duplicate-preview-fix-20260826)
// ==============================

/**
 * `messagePreview` が `message` の複製になる通知種別 (allowlist)。
 *
 * これらの writer は送受信したメッセージ本文を `message` (先頭 100 字) と
 * `messagePreview` (先頭 200 字) の **両方** へ入れる。どちらのフィールドも別 consumer
 * (`message` = メール本文 / `messagePreview` = 対応履歴 CSV・新着ポップアップ) が使うため
 * BE 側では落とせず、**両方を同時に描画するドロワーだけ**が同一本文の 2 行表示になる。
 *
 * **既定は「抑止しない」**。ここに無い種別は 2 フィールドの意味が異なりうる
 * (例: `item.rejected` は `message` = 誰が何をしたか / `messagePreview` = 差し戻し理由) ため、
 * 従来どおり必ず両方描画する。将来 `NotificationType` が増えても自動では対象にならない。
 */
const DUPLICATE_PREVIEW_TYPES: ReadonlySet<string> = new Set([
  'portal.message_sent',
  'portal.message_received',
  'cw.message_sent',
  'line.message_sent',
  'client.message_received',
]);

/** 本文プレビュー行の判定に使う入力。`Notification` の必要な field だけを要求する。 */
export interface NotificationPreviewInput {
  type?: string | null;
  message?: string | null;
  messagePreview?: string | null;
}

/**
 * ドロワーに出す本文プレビュー行を返す。描画しない場合は null。
 *
 * - `messagePreview` が未設定 / 空白のみ → null (旧実装は空白のみでも truthy で空行が出た)
 * - 上記 allowlist の種別で **raw value が `messagePreview.startsWith(message)`**
 *   (完全一致を含む) → 同一本文の重複なので null
 * - それ以外 → `messagePreview` を **そのまま** 返す (文字列は切り詰めない。表示上の省略は
 *   呼び出し側の `truncate` が担う)
 *
 * 判定材料は種別と 2 field の raw value だけで、正規化も本文解析も行わない
 * (表示文言を変えても判定が変わらないようにするため)。
 */
export function resolveNotificationPreviewLine(
  n: NotificationPreviewInput | null | undefined,
): string | null {
  const preview = typeof n?.messagePreview === 'string' ? n.messagePreview : '';
  if (!preview.trim()) return null;

  if (!DUPLICATE_PREVIEW_TYPES.has(trimmed(n?.type))) return preview;

  const message = typeof n?.message === 'string' ? n.message : '';
  if (!message.trim()) return preview;

  // `message` は `messagePreview` の先頭 N 字なので、完全一致も prefix 一致に含まれる。
  return preview.startsWith(message) ? null : preview;
}
